/**
 * Lift2Fit — admin: Invitations (`#/admin/invitations`).
 *
 * The "free group" idea: mint an invitation that lets someone create a profile
 * as a LIFTER or as a COACH, optionally on a free/trial catalog plan, with an
 * optional email, note and batch/group label. Each one produces a shareable
 * link the founder can send. Acceptance reuses the registration path and is
 * single-use, expiring, and can never grant admin.
 */
import { useEffect, useState } from 'react';
import {
  api,
  type AdminAuditEntry,
  type AdminInvitationView,
  type InvitationProfileKind,
  type PlanCatalogEntry,
} from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, SparkIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  LiveMessage,
  Panel,
  ScreenHeader,
  StatusChip,
  type ChipTone,
} from '../components';
import { formatDateShort } from '../crm/shared';
import { useAsync } from '../hooks';
import { ADMIN_EYEBROW, formatCount } from './shared';

const STATUS_TONE: Record<AdminInvitationView['status'], ChipTone> = {
  pending: 'accent',
  accepted: 'success',
  expired: 'warning',
  revoked: 'neutral',
};

export function AdminInvitationsScreen() {
  const state = useAsync(() => api.adminInvitations(), []);
  const plansState = useAsync(() => api.adminPlans(), []);
  const [invitations, setInvitations] = useState<AdminInvitationView[]>([]);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [audit, setAudit] = useState<AdminAuditEntry | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [profileKind, setProfileKind] = useState<InvitationProfileKind>('lifter');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [groupLabel, setGroupLabel] = useState('free-trial');
  const [planId, setPlanId] = useState('');
  const [grantDays, setGrantDays] = useState('30');
  const [grantSeats, setGrantSeats] = useState('10');
  const [expiresInDays, setExpiresInDays] = useState('14');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) setInvitations(state.data.data);
  }, [state.data]);

  const plans: PlanCatalogEntry[] = (plansState.data?.data ?? []).filter(
    (plan) => plan.audience === profileKind && plan.active,
  );

  const create = async () => {
    setCreateBusy(true);
    setCreateError(null);
    const days = grantDays.trim() === '' ? null : Number(grantDays);
    const seats = grantSeats.trim() === '' ? null : Number(grantSeats);
    const expiry = Number(expiresInDays);
    const result = await api.adminCreateInvitation({
      profileKind,
      email: email.trim() === '' ? null : email.trim(),
      note: note.trim(),
      groupLabel: groupLabel.trim() === '' ? null : groupLabel.trim(),
      planId: planId === '' ? null : planId,
      grantDays: days !== null && Number.isInteger(days) && days > 0 ? days : null,
      grantSeats: seats !== null && Number.isInteger(seats) && seats > 0 ? seats : null,
      expiresInDays: Number.isInteger(expiry) && expiry > 0 ? expiry : 14,
    });
    setCreateBusy(false);
    if (result.problem || !result.data) {
      setCreateError(result.problem?.detail ?? result.problem?.title ?? 'The invitation was not created.');
      return;
    }
    setInvitations((current) => [result.data!.invitation, ...current]);
    setShareUrl(result.data.shareUrl);
    setAudit(result.data.audit);
    setNotice(`Invitation ${result.data.invitation.code} is ready to share.`);
    setEmail('');
    setNote('');
  };

  const revoke = async (invitation: AdminInvitationView) => {
    setBusyId(invitation.invitationId);
    setNotice(null);
    const result = await api.adminRevokeInvitation(invitation.invitationId);
    setBusyId(null);
    if (result.problem || !result.data) {
      setNotice(result.problem?.detail ?? result.problem?.title ?? 'The invitation was not revoked.');
      return;
    }
    setInvitations((current) =>
      current.map((entry) =>
        entry.invitationId === invitation.invitationId ? result.data!.invitation : entry,
      ),
    );
    setAudit(result.data.audit);
    setNotice(`Revoked ${result.data.invitation.code}.`);
  };

  const copy = async () => {
    if (shareUrl === null) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setNotice('Link copied to the clipboard.');
    } catch {
      setNotice('Copy failed — select the link and copy it manually.');
    }
  };

  if (state.loading && invitations.length === 0) {
    return <LoadingPanel label="Loading invitations" rows={4} />;
  }

  if (state.error && invitations.length === 0) {
    return (
      <EmptyState
        title="The invitation list did not load"
        body={state.error}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-invitations">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Invitations"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Invite someone to try Lift2Fit in a free group. The link creates a profile as a lifter or as a coach, optionally on a free/trial plan. It is single-use, expiring, and can never grant admin."
        actions={
          <AppLinkButton to="/admin/plans" variant="outline">
            Plans & pricing
          </AppLinkButton>
        }
      />

      {notice ? <LiveMessage message={notice} tone="success" /> : null}

      <Panel title="Create an invitation" subtitle="The share link opens the public #/join/:code screen." testId="admin-invitation-new">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Profile</span>
            <select
              data-testid="admin-invitation-new-kind"
              value={profileKind}
              onChange={(event) => {
                const next = event.target.value as InvitationProfileKind;
                setProfileKind(next);
                setPlanId('');
              }}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            >
              <option value="lifter">Lifter</option>
              <option value="coach">Coach</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email (optional)</span>
            <input
              data-testid="admin-invitation-new-email"
              type="email"
              value={email}
              placeholder="tester@example.com"
              onChange={(event) => setEmail(event.target.value)}
              className="h-9 w-52 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Group label</span>
            <input
              data-testid="admin-invitation-new-group"
              value={groupLabel}
              onChange={(event) => setGroupLabel(event.target.value)}
              className="h-9 w-36 border border-hairline bg-page px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Free plan</span>
            <select
              data-testid="admin-invitation-new-plan"
              value={planId}
              onChange={(event) => setPlanId(event.target.value)}
              className="h-9 border border-hairline bg-page px-2 text-[13px] text-ink"
            >
              <option value="">No plan attached</option>
              {plans.map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.displayName}
                  {plan.priceMonthly > 0 ? ` (list ${plan.currency} ${plan.priceMonthly})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Trial days</span>
            <input
              data-testid="admin-invitation-new-days"
              value={grantDays}
              inputMode="numeric"
              onChange={(event) => setGrantDays(event.target.value)}
              className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Seats</span>
            <input
              data-testid="admin-invitation-new-seats"
              value={grantSeats}
              inputMode="numeric"
              onChange={(event) => setGrantSeats(event.target.value)}
              className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Expires (days)</span>
            <input
              data-testid="admin-invitation-new-expiry"
              value={expiresInDays}
              inputMode="numeric"
              onChange={(event) => setExpiresInDays(event.target.value)}
              className="l2f-tabular h-9 w-20 border border-hairline bg-page px-2 font-mono text-[13px] text-ink"
            />
          </label>
          <button
            type="button"
            data-testid="admin-invitation-new-create"
            disabled={createBusy}
            onClick={() => void create()}
            className="inline-flex h-9 items-center bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
          >
            {createBusy ? 'Creating…' : 'Create invitation'}
          </button>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Note (optional)</span>
          <input
            data-testid="admin-invitation-new-note"
            value={note}
            placeholder="Beta tester — asked about the conditioning block"
            onChange={(event) => setNote(event.target.value)}
            className="h-9 w-full border border-hairline bg-page px-2 text-[13px] text-ink"
          />
        </label>
        {createError ? (
          <p role="alert" data-testid="admin-invitation-new-error" className="text-[12px] text-danger">
            {createError}
          </p>
        ) : null}

        {shareUrl ? (
          <div
            className="flex flex-wrap items-center gap-3 border-l-2 border-accent bg-page-subtle px-3 py-2"
            data-testid="admin-invitation-share"
          >
            <SparkIcon className="text-[14px] text-accent" />
            <code className="min-w-0 flex-1 break-all text-[12px] text-ink">{shareUrl}</code>
            <button
              type="button"
              data-testid="admin-invitation-copy"
              onClick={() => void copy()}
              className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Copy link
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title="All invitations" subtitle="Newest first. Revoke only applies while an invitation is pending." testId="admin-invitation-list">
        <p className="text-[12px] text-ink-muted" data-testid="admin-invitation-total">
          {formatCount(invitations.length)} invitation(s)
        </p>
        {invitations.length === 0 ? (
          <EmptyState
            title="No invitations yet"
            body="Create the first one above and send the link to a tester."
            icon={<SparkIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Code</th>
                  <th className="py-2 pr-3 font-bold">Profile</th>
                  <th className="py-2 pr-3 font-bold">Group</th>
                  <th className="py-2 pr-3 font-bold">Free plan</th>
                  <th className="py-2 pr-3 font-bold">Status</th>
                  <th className="py-2 pr-3 font-bold">Expires</th>
                  <th className="py-2 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation) => (
                  <tr
                    key={invitation.invitationId}
                    data-testid={`admin-invitation-row-${invitation.invitationId}`}
                    className="border-b border-hairline last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-3">
                      <span className="flex min-w-0 flex-col">
                        <span className="font-mono text-[13px] font-bold text-ink">{invitation.code}</span>
                        <span className="text-[12px] text-ink-muted">{invitation.email ?? 'open link'}</span>
                        {invitation.note ? (
                          <span className="max-w-[34ch] text-[11px] text-ink-muted">{invitation.note}</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-[12px] capitalize text-ink">{invitation.profileKind}</td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">{invitation.groupLabel ?? '—'}</td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">
                      {invitation.planName === null ? '—' : invitation.planName}
                      {invitation.grantDays !== null ? ` · ${invitation.grantDays}d` : ''}
                      {invitation.profileKind === 'coach' && invitation.grantSeats !== null
                        ? ` · ${invitation.grantSeats} seats`
                        : ''}
                    </td>
                    <td className="py-3 pr-3" data-testid={`admin-invitation-state-${invitation.invitationId}`}>
                      <StatusChip tone={STATUS_TONE[invitation.status]}>{invitation.status}</StatusChip>
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink-muted">
                      {formatDateShort(invitation.expiresAt)}
                      {invitation.acceptedAt ? ` · accepted ${formatDate(invitation.acceptedAt)}` : ''}
                    </td>
                    <td className="py-3">
                      {invitation.status === 'pending' ? (
                        <button
                          type="button"
                          data-testid={`admin-invitation-revoke-${invitation.invitationId}`}
                          disabled={busyId === invitation.invitationId}
                          onClick={() => void revoke(invitation)}
                          className="inline-flex h-9 items-center border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-danger hover:border-danger disabled:opacity-40"
                        >
                          {busyId === invitation.invitationId ? 'Writing…' : 'Revoke'}
                        </button>
                      ) : (
                        <span className="text-[11px] uppercase tracking-[0.08em] text-ink-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {audit ? (
        <p
          className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[12px] text-ink-muted"
          data-testid="admin-invitations-last-audit"
        >
          Audited: <span className="font-bold text-ink">{audit.summary}</span> · {audit.actorEmail ?? 'admin'} ·{' '}
          {formatDate(audit.createdAt)}
        </p>
      ) : null}
    </div>
  );
}
